import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TaskBoardView,
  TaskListView,
  TaskCalendarView,
  TaskGanttView,
  TaskTimelineView,
  TimelineViewControls,
  TaskDocumentsView,
  BoardActivityView,
  TaskHeader,
  useTaskListConfig,
  useSharedListViewConfig,
  useTaskFilters,
  useTaskShowCompleted,
  useTaskShowArchived,
  useTimelineZoom,
  useLastActiveBoardView,
  viewTypeToSlug,
  useBoardRealtimeEvents,
} from '@/features/tasks/public';
import { BoardDashboardView } from '@/features/dashboard/public';
import { WhiteboardView } from '@/features/whiteboards/public';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import type { BoardView, BoardViewInfo } from '@/features/tasks/public';
import { useConfirm } from '@/shared/hooks';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks';
import { useBoards } from '../hooks/useBoards';
import { useBoardViews } from '../hooks/useBoardViews';
import { useDeleteBoardView } from '../hooks/useBoardViewMutations';
import { AddViewPopover } from '../components/AddViewPopover';
import { BoardNoViewsEmptyState } from '../components/BoardNoViewsEmptyState';

export default function BoardPage() {
  const { t } = useTranslation('boards');
  const { toast } = useToast();
  const {
    orgId: _orgId,
    projectId,
    boardId,
  } = useParams<{
    orgId: string;
    projectId: string;
    boardId: string;
  }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const { lastActiveView, setLastActiveView } = useLastActiveBoardView(boardId!);
  const viewParam = searchParams.get('view') as BoardView | null;
  const activeView = viewParam ?? lastActiveView ?? 'list';

  function setView(v: BoardView) {
    setSearchParams({ view: v }, { replace: true });
    setLastActiveView(v);
  }

  const { listConfig, setListConfig } = useTaskListConfig(boardId!);
  const { visibleColumns, columnWidths, setVisibleColumns, setColumnWidths } =
    useSharedListViewConfig(boardId!);
  const { filters, setFilters } = useTaskFilters(boardId!);
  const { showCompleted, setShowCompleted } = useTaskShowCompleted(boardId!);
  const { showArchived, setShowArchived } = useTaskShowArchived(boardId!);
  const { zoom: timelineZoom, setZoom: setTimelineZoom } = useTimelineZoom(boardId!);
  // Bumped by the toolbar's "Hoy" button; TaskTimelineView re-centers on each change.
  const [timelineTodayNonce, setTimelineTodayNonce] = useState(0);

  const { boards } = useBoards(projectId!);
  const board = boards.find((b) => b.id === boardId);

  // Subscribe to real-time board events via SignalR.
  useBoardRealtimeEvents(projectId!, boardId!);

  const { views, isLoading: viewsLoading } = useBoardViews(boardId!);
  const deleteView = useDeleteBoardView(boardId!);

  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  const boardViewInfos: BoardViewInfo[] = views.map((v) => ({
    id: v.id ?? '',
    type: v.type,
    name: v.name,
  }));

  const existingTypes = boardViewInfos.map((v) => v.type);

  // Redirect to first available view when no explicit preference exists, or when activeView is unavailable
  useEffect(() => {
    if (viewsLoading || views.length === 0) return;
    const slugs = views.map((v) => viewTypeToSlug(v.type));
    const noPreference = viewParam === null && !lastActiveView;
    if (noPreference || !slugs.includes(activeView)) {
      setSearchParams({ view: slugs[0] }, { replace: true });
    }
  }, [views, viewsLoading, viewParam, lastActiveView, activeView, setSearchParams]);

  async function handleRemoveView(viewId: string) {
    const { confirmed } = await confirm({
      title: t('views.confirmTitle'),
      description: t('views.confirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await deleteView.mutateAsync(viewId);
      toast.success({ title: t('views.toast.deleteSuccess') });
    } catch {
      // error handled by global handler
    }
  }

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="flex h-full flex-col" data-testid="boards-board-page">
        <TaskHeader
          projectId={projectId!}
          boardId={boardId!}
          activeView={activeView}
          onViewChange={setView}
          config={{ ...listConfig, visibleColumns, showCompleted, showArchived }}
          filters={filters}
          onGroupByChange={(groupBy) => setListConfig((prev) => ({ ...prev, groupBy }))}
          onToggleCompleted={setShowCompleted}
          onToggleArchived={setShowArchived}
          onFiltersChange={setFilters}
          boardName={board?.name ?? undefined}
          boardViews={boardViewInfos}
          boardViewsLoading={viewsLoading}
          onRemoveView={handleRemoveView}
          addViewControl={<AddViewPopover boardId={boardId!} existingTypes={existingTypes} />}
          viewControls={
            activeView === 'timeline' ? (
              <TimelineViewControls
                zoom={timelineZoom}
                onZoomChange={setTimelineZoom}
                onTodayClick={() => setTimelineTodayNonce((n) => n + 1)}
              />
            ) : undefined
          }
        />

        <div className="min-h-0 flex-1 overflow-auto">
          {!viewsLoading && views.length === 0 && <BoardNoViewsEmptyState />}
          {activeView === 'board' && views.length > 0 && (
            <TaskBoardView projectId={projectId!} boardId={boardId!} />
          )}
          {activeView === 'list' && views.length > 0 && (
            <TaskListView
              projectId={projectId!}
              boardId={boardId!}
              config={{ ...listConfig, visibleColumns, showCompleted, showArchived }}
              columnWidths={columnWidths}
              filters={filters}
              onConfigChange={setListConfig}
              onColumnsChange={setVisibleColumns}
              onColumnWidthsChange={setColumnWidths}
            />
          )}
          {activeView === 'calendar' && views.length > 0 && (
            <TaskCalendarView projectId={projectId!} boardId={boardId!} filters={filters} />
          )}
          {activeView === 'gantt' && views.length > 0 && (
            <TaskGanttView
              projectId={projectId!}
              boardId={boardId!}
              filters={filters}
              showCompleted={showCompleted}
              onShowCompletedChange={setShowCompleted}
            />
          )}
          {activeView === 'documents' && <TaskDocumentsView projectId={projectId!} />}
          {activeView === 'dashboard' && views.length > 0 && <BoardDashboardView />}
          {activeView === 'activity' && views.length > 0 && (
            <BoardActivityView projectId={projectId!} boardId={boardId!} />
          )}
          {activeView === 'timeline' && views.length > 0 && (
            <TaskTimelineView
              projectId={projectId!}
              boardId={boardId!}
              filters={filters}
              showCompleted={showCompleted}
              groupBy={listConfig.groupBy}
              zoom={timelineZoom}
              scrollToTodayNonce={timelineTodayNonce}
            />
          )}
          {activeView === 'whiteboard' && views.length > 0 && (
            <WhiteboardView projectId={projectId!} boardId={boardId!} />
          )}
        </div>

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(open) => {
            if (!open) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          variant={confirmOptions?.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </div>
    </FeatureErrorBoundary>
  );
}
